/**
 * dt-badge - Status badge with color variants
 * Variants: success, warning, error, info
 *
 * Uses DOM node manipulation. The <span> wrapper is built once;
 * variant changes update its className only.
 */
import { BaseElement } from '../base-element';

export class DtBadge extends BaseElement {
  static observedAttributes = ['variant'];

  private _variant: 'success' | 'warning' | 'error' | 'info' = 'info';
  private _spanEl: HTMLElement | null = null;
  private _built = false;

  get variant(): string {
    return this._variant;
  }

  set variant(value: string) {
    if (['success', 'warning', 'error', 'info'].includes(value)) {
      this._variant = value as 'success' | 'warning' | 'error' | 'info';
      if (this._spanEl) {
        this._spanEl.className = this.getVariantClasses();
      }
    }
  }

  connectedCallback(): void {
    if (!this._built) {
      this.buildBadge();
    }
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'variant') {
      this.variant = newValue || 'info';
    }
  }

  private getVariantClasses(): string {
    const base = 'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ring-1 ring-inset';

    switch (this._variant) {
      case 'success':
        return `${base} bg-success-50 text-success-700 ring-neutral-300`;
      case 'warning':
        return `${base} bg-warning-50 text-warning-700 ring-neutral-300`;
      case 'error':
        return `${base} bg-error-50 text-error-700 ring-neutral-300`;
      case 'info':
      default:
        return `${base} bg-info-50 text-info-700 ring-neutral-300`;
    }
  }

  private buildBadge(): void {
    this._spanEl = document.createElement('span');
    this._spanEl.className = this.getVariantClasses();
    this.adoptChildrenInto(this._spanEl);
    this.appendChild(this._spanEl);
    this._built = true;
  }
}

customElements.define('dt-badge', DtBadge);
